import { getState, setState } from "./store";
import type { SeatingHistoryEntry, SeatingAssignment } from "../types/models";

const MAX_HISTORY_SIZE = 50;

/**
 * Get the current seating assignment for the active chart
 */
function getCurrentAssignment(classId: string): SeatingAssignment | null {
    const s = getState();
    const cls = s.classes.find(c => c.id === classId);
    if (!cls?.activeChartId) return null;

    const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId);
    return chart?.lastAssignment ?? null;
}

/**
 * Save current seating state before modification
 */
export function pushSeatingHistory(classId: string, action: string): void {
    setState(s => {
        const cls = s.classes.find(c => c.id === classId);
        if (!cls?.activeChartId) return;

        const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId);
        if (!chart?.lastAssignment) return;

        // Initialize seating history if missing
        if (!cls.seatingHistory) {
            cls.seatingHistory = { past: [], future: [] };
        }

        // Create snapshot of current state
        const entry: SeatingHistoryEntry = {
            assignment: {
                seatAssignments: new Map(chart.lastAssignment.seatAssignments),
                timestamp: chart.lastAssignment.timestamp
            },
            timestamp: Date.now(),
            action
        };

        // Push to past stack
        cls.seatingHistory.past.push(entry);

        // Clear future (new action invalidates redo)
        cls.seatingHistory.future = [];

        // Trim to max size
        if (cls.seatingHistory.past.length > MAX_HISTORY_SIZE) {
            cls.seatingHistory.past.shift();
        }
    });
}

/**
 * Check if seating undo is available
 */
export function canUndoSeating(classId: string): boolean {
    const s = getState();
    const cls = s.classes.find(c => c.id === classId);
    return (cls?.seatingHistory?.past.length ?? 0) > 0;
}

/**
 * Check if seating redo is available
 */
export function canRedoSeating(classId: string): boolean {
    const s = getState();
    const cls = s.classes.find(c => c.id === classId);
    return (cls?.seatingHistory?.future.length ?? 0) > 0;
}

/**
 * Restore previous seating state
 */
export function undoSeating(classId: string): void {
    setState(s => {
        const cls = s.classes.find(c => c.id === classId);
        if (!cls?.seatingHistory || cls.seatingHistory.past.length === 0) return;
        if (!cls.activeChartId) return;

        const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId);
        if (!chart?.lastAssignment) return;

        // Save current to future
        const current: SeatingHistoryEntry = {
            assignment: {
                seatAssignments: new Map(chart.lastAssignment.seatAssignments),
                timestamp: chart.lastAssignment.timestamp
            },
            timestamp: Date.now(),
            action: "current"
        };
        cls.seatingHistory.future.push(current);

        // Pop from past and restore
        const prev = cls.seatingHistory.past.pop()!;
        chart.lastAssignment = {
            seatAssignments: new Map(prev.assignment.seatAssignments),
            timestamp: prev.assignment.timestamp
        };
    });

    // Trigger re-render
    document.dispatchEvent(new CustomEvent("seating:update"));
}

/**
 * Restore next seating state
 */
export function redoSeating(classId: string): void {
    setState(s => {
        const cls = s.classes.find(c => c.id === classId);
        if (!cls?.seatingHistory || cls.seatingHistory.future.length === 0) return;
        if (!cls.activeChartId) return;

        const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId);
        if (!chart?.lastAssignment) return;

        // Save current to past
        const current: SeatingHistoryEntry = {
            assignment: {
                seatAssignments: new Map(chart.lastAssignment.seatAssignments),
                timestamp: chart.lastAssignment.timestamp
            },
            timestamp: Date.now(),
            action: "current"
        };
        cls.seatingHistory.past.push(current);

        // Pop from future and restore
        const next = cls.seatingHistory.future.pop()!;
        chart.lastAssignment = {
            seatAssignments: new Map(next.assignment.seatAssignments),
            timestamp: next.assignment.timestamp
        };
    });

    // Trigger re-render
    document.dispatchEvent(new CustomEvent("seating:update"));
}
